import {
  boundDay,
  formatTimeDisplay,
  hasZone,
  impossibleDateMessage,
  invalidTextMessage,
  MONTH_NAMES,
  parseIsoDate,
  SbDateTimeMode,
  SbDateValueAs,
  SbFieldReading,
} from './sb-date-time-format';
import { daysInMonth, parseLooseDate, parseLooseDateTime, parseLooseTime, SbDateParts, SbTimeParts } from './sb-date-time-parse';

/**
 * The segmented date and time field as numbers: month, day, year, hour, minute and AM/PM, each a
 * segment of its own, as the browser's `<input type=date>` and React Aria's DateField hold them.
 * There is no text to edit: a digit goes only into the focused segment, which moves focus on when
 * no further digit can follow; Up and Down step it; Backspace empties it; a key it cannot take is
 * refused and blocks it until it is cleared; a paste is read whole by `sb-date-time-parse.ts`.
 * The field component renders a state and turns events into these calls; it holds the date in no
 * other form. Pure: no Angular, no DOM; today and now come in through `SbSegContext`.
 */
export type SegKind = 'month' | 'day' | 'year' | 'hour' | 'minute' | 'period';

export interface Seg {
  readonly kind: SegKind;
  /** Month 1-12, day 1-31, year, hour 1-12, minute 0-59, period 0 (AM) or 1 (PM); null while empty or unfinished. */
  readonly value: number | null;
  /** The digits typed since focus entered the segment. */
  readonly typed: string;
  /** Why a key was refused here, while the segment is refused. */
  readonly refused: string | null;
}

export type SbYearFirst = 'off' | 'year' | 'needsSeparator' | 'on';

export interface FieldState {
  readonly mode: SbDateTimeMode;
  readonly segs: readonly Seg[];
  /** Stored text the field cannot read, shown as it is until the first edit. */
  readonly kept: string | null;
  /** The focused segment; -1 while focus is elsewhere. */
  readonly focus: number;
  /** How focus reached the segment: separators right after an auto-advance belong to the segment before. */
  readonly enteredBy: 'user' | 'advance';
  /**
   * Typing reached the segment on from the field's first one (or in a field emptied by Clear): only
   * such a run goes on past the date, into the time or a range's To half. A date typed from another
   * segment of a held value stops at the end of the date, so it never runs into another part.
   */
  readonly run: boolean;
  /** Nothing typed into the focused segment since focus entered it: the next digit replaces its value. */
  readonly fresh: boolean;
  /** A date being typed year first (section 3.9 of the spec): started only in an empty date, over once the date is whole or the field is left. */
  readonly yearFirst: SbYearFirst;
  /** Why the last paste did not read, until the next key or until the field is left. */
  readonly rejected: string | null;
  /**
   * The user emptied the AM/PM that went with the time (Backspace or Delete): the AM/PM rule does
   * not fill it again, since nobody chose that half of the day. An A or P, a step, a pick, or an
   * hour typed or chosen anew ends it.
   */
  readonly periodCleared: boolean;
}

/** Today and now in the field's zone. */
export interface SbSegContext {
  today: SbDateParts;
  now: SbTimeParts;
}

export type SbSegReading =
  | { kind: 'empty' }
  | { kind: 'complete'; date: SbDateParts | null; time: SbTimeParts | null }
  | { kind: 'incomplete' }
  | { kind: 'impossible'; why: string }
  | { kind: 'refused'; seg: number; why: string }
  | { kind: 'kept'; text: string };

/** What a key did: the new state, a refusal or line to announce, and whether focus would leave the field. */
export interface SbKeyResult {
  state: FieldState;
  refusal?: string;
  announce?: string;
  /** Tab past the last segment or Shift+Tab before the first: the browser moves focus on. */
  exit?: 'forward' | 'back';
  /** The last segment in typing order was completed by a run typed from the first (a range then moves to its To half). */
  filled?: boolean;
}

const KINDS: Record<SbDateTimeMode, readonly SegKind[]> = {
  date: ['month', 'day', 'year'],
  time: ['hour', 'minute', 'period'],
  datetime: ['month', 'day', 'year', 'hour', 'minute', 'period'],
};

/** The literal after each segment but the last. */
const LITERALS: Record<SbDateTimeMode, readonly string[]> = {
  date: ['/', '/'],
  time: [':', ' '],
  datetime: ['/', '/', ', ', ':', ' '],
};

export const SEGMENT_PLACEHOLDER: Record<SegKind, string> = { month: 'mm', day: 'dd', year: 'yyyy', hour: 'hh', minute: 'mm', period: 'aa' };

export const SEGMENT_NAME: Record<SegKind, string> = { month: 'Month', day: 'Day', year: 'Year', hour: 'Hour', minute: 'Minute', period: 'AM/PM' };

const DATE_KINDS: ReadonlySet<SegKind> = new Set(['month', 'day', 'year']);

export const YEAR_MIN = 1900;
export const YEAR_MAX = 2099;
const yearMessage = (typed: string): string => `No year starts with ${typed}; years are ${YEAR_MIN} to ${YEAR_MAX}`;
const YEAR_FIRST_LINE = 'Year first: type the year, then - or /';
/** Why typed digits name no hour: "00" or a 24-hour hour. The field and the hour column say it alike. */
export const noHourMessage = (typed: string): string =>
  Number(typed) === 0 ? 'There is no hour 00; use 12 AM' : `There is no hour ${typed}: hours are 1 to 12, then A or P`;

const YEAR_FIRST_MESSAGE = 'Type - or / after the year to type a date year first (2026-11-03), or type the month first (11/03/2026)';
const DOT_MESSAGE = 'Type / or - between the month, day and year: dates here are month first (11/03/2026)';
const BLOCKED_SUFFIX = ' (press Backspace to type again)';

/** React Aria's PageUp/PageDown steps. */
const PAGE_STEP: Record<SegKind, number> = { year: 5, month: 2, day: 7, hour: 2, minute: 15, period: 1 };

const SEPARATORS = new Set(['/', '-', '.', ',', ':', ' ', 'T', 't']);
const YEAR_FIRST_SEPARATORS = new Set(['-', '/', '.']);

/**
 * The separators that can stand before each segment: a date's between its parts (and before a
 * range's To month), a datetime's between the date and the hour, a time's before the minutes and
 * before AM/PM. A date separator never carries typing on into the time, nor a time's into a date.
 */
const LEADS: Record<SegKind, string> = { month: '/- ', day: '/- ', year: '/- ', hour: ', Tt', minute: ':.', period: ' ' };

/**
 * What can stand before a segment of this state. A dot separates a date's parts only in a date
 * typed year first (2026.11.03): typed from the month, 03.11.2026 is a day-first date, which the
 * paste reader reads as 3 November, so the month-first segments refuse it rather than make 11 March.
 */
const leads = (state: FieldState, kind: SegKind): string => LEADS[kind] + (DATE_KINDS.has(kind) && state.yearFirst !== 'off' ? '.' : '');

const pad = (value: number): string => String(value).padStart(2, '0');

const emptySeg = (kind: SegKind): Seg => ({ kind, value: null, typed: '', refused: null });

export const emptyState = (mode: SbDateTimeMode): FieldState => ({
  mode,
  segs: KINDS[mode].map(emptySeg),
  kept: null,
  focus: -1,
  enteredBy: 'user',
  run: true,
  fresh: true,
  yearFirst: 'off',
  rejected: null,
  periodCleared: false,
});

export const segmentKinds = (mode: SbDateTimeMode): readonly SegKind[] => KINDS[mode];

export const segmentLiterals = (mode: SbDateTimeMode): readonly string[] => LITERALS[mode];

const indexOf = (state: FieldState, kind: SegKind): number => state.segs.findIndex((seg) => seg.kind === kind);

/** A segment's value while it is not refused. */
const valueOf = (state: FieldState, kind: SegKind): number | null => {
  const seg = state.segs.find((s) => s.kind === kind);
  return seg && !seg.refused ? seg.value : null;
};

const isBlank = (seg: Seg): boolean => seg.value === null && !seg.typed && !seg.refused;

const withSeg = (state: FieldState, index: number, seg: Partial<Seg>): FieldState => ({
  ...state,
  segs: state.segs.map((s, i) => (i === index ? { ...s, ...seg } : s)),
});

/** Every date segment empty: where a date typed year first may start. */
const dateBlank = (state: FieldState, except = -1): boolean =>
  state.mode !== 'time' && state.segs.every((seg, i) => i === except || !DATE_KINDS.has(seg.kind) || isBlank(seg));

const timeBlank = (state: FieldState): boolean => state.segs.every((seg) => DATE_KINDS.has(seg.kind) || isBlank(seg));

/** The days the day segment can hold: 31 while the month is not known, 29 for a February with no year. */
const dayLimit = (state: FieldState): number => {
  const month = valueOf(state, 'month');
  if (month === null) {
    return 31;
  }
  const year = valueOf(state, 'year');
  return month === 2 && year === null ? 29 : daysInMonth(year ?? 2001, month - 1);
};

/** The order typing fills the segments in: the year first once a date is typed year first. */
const typingOrder = (state: FieldState): number[] => {
  const all = state.segs.map((_, i) => i);
  if (state.yearFirst === 'off' || state.mode === 'time') {
    return all;
  }
  const year = indexOf(state, 'year');
  return [year, ...all.filter((i) => i !== year)];
};

const nextInOrder = (state: FieldState, index: number): number => {
  const order = typingOrder(state);
  return order[order.indexOf(index) + 1] ?? -1;
};

/** A segment the user enters starts a run only as the first one; a segment typing moves to carries the run on. */
const enter = (state: FieldState, index: number, by: 'user' | 'advance'): FieldState => ({
  ...state,
  focus: index,
  enteredBy: by,
  run: by === 'advance' ? state.run : index === 0,
  fresh: true,
});

/**
 * Focus moves on to the next segment in typing order. The last one stays, and says so when a run
 * typed from the first filled it; the end of the date stays too unless such a run reached it.
 */
const advance = (state: FieldState, from: number): SbKeyResult => {
  const next = nextInOrder(state, from);
  const endsDate = DATE_KINDS.has(state.segs[from].kind) && (next < 0 || !DATE_KINDS.has(state.segs[next].kind));
  if (next < 0 || (endsDate && !state.run)) {
    return { state: { ...state, fresh: false }, filled: next < 0 && state.run };
  }
  return { state: enter(state, next, 'advance') };
};

const refuse = (state: FieldState, index: number, why: string): SbKeyResult => ({
  state: withSeg({ ...state, fresh: false }, index, { refused: why }),
  refusal: why,
});

/** The value a waiting buffer stands for when the user moves on, or null when it is not a whole value. */
const wholeValue = (kind: SegKind, typed: string): number | null => {
  if (!typed) {
    return null;
  }
  const n = Number(typed);
  switch (kind) {
    case 'month':
    case 'hour':
      return n >= 1 ? n : null;
    case 'day':
      return n >= 1 ? n : null;
    case 'year':
      return typed.length === 4 ? n : null;
    case 'minute':
      return typed.length === 2 ? n : null;
    default:
      return null;
  }
};

/** Why a separator cannot end what is waiting in a segment. */
const unfinishedMessage = (kind: SegKind, typed: string): string => {
  if (kind === 'year') {
    return 'Type four digits for the year';
  }
  if (kind === 'minute') {
    return 'Type two digits for the minutes';
  }
  return `There is no ${kind} ${typed}`;
};

/** An explicit move (Tab, Left, Right, a click) completes a waiting digit that is a whole value; anything else stays unfinished. */
const completeWaiting = (state: FieldState): FieldState => {
  const index = state.focus;
  const seg = state.segs[index];
  if (!seg || state.fresh || seg.refused || seg.value !== null || !seg.typed) {
    return state;
  }
  const value = wholeValue(seg.kind, seg.typed);
  return value === null ? state : withSeg(state, index, { value });
};

/** Month, day and year all hold a value. */
const dateWhole = (state: FieldState): boolean =>
  state.mode !== 'time' && valueOf(state, 'month') !== null && valueOf(state, 'day') !== null && valueOf(state, 'year') !== null;

/**
 * A date typed year first is over once the date is empty again or whole: the key that completed it
 * has already moved on in the year-first order, and from then on the segments go in their own
 * order (a year typed again stays the year, or moves on to the hour).
 */
const settle = (state: FieldState): FieldState =>
  state.yearFirst !== 'off' && (dateBlank(state) || dateWhole(state)) ? { ...state, yearFirst: 'off' } : state;

// ─── Digits ─────────────────────────────────────────────────

const done = (state: FieldState, index: number, typed: string, value: number): SbKeyResult => {
  const next = withSeg(state, index, { value, typed, refused: null });
  if (state.segs[index].kind === 'year' && state.yearFirst === 'year') {
    return { state: { ...next, yearFirst: 'needsSeparator', fresh: false } };
  }
  // An hour typed is a time typed: the AM/PM rule may fill an AM/PM the user emptied before it.
  return advance(state.segs[index].kind === 'hour' ? { ...next, periodCleared: false } : next, index);
};

const wait = (state: FieldState, index: number, typed: string): SbKeyResult => ({
  state: withSeg({ ...state, fresh: false }, index, { value: null, typed, refused: null }),
});

const typeDigit = (state: FieldState, digit: string): SbKeyResult => {
  const index = state.focus;
  const seg = state.segs[index];
  if (seg.refused && !state.fresh) {
    return refuse(state, index, seg.refused);
  }
  if (!state.fresh && seg.value !== null && seg.kind !== 'period') {
    // Completed by typing with nowhere to move on to: no digit is added to a whole part.
    return refuse(state, index, seg.kind === 'year' && state.yearFirst === 'needsSeparator' ? YEAR_FIRST_MESSAGE : `The ${seg.kind} is complete`);
  }
  const buffer = state.fresh ? '' : seg.typed;
  const d = Number(digit);
  const typed = buffer + digit;
  const n = Number(typed);
  switch (seg.kind) {
    case 'month': {
      if (!buffer) {
        if (d === 0 || d === 1 || (d === 2 && dateBlank(state, index))) {
          return wait(state, index, typed);
        }
        return done(state, index, typed, d);
      }
      if (buffer === '2') {
        // The deferred February: a 0 starts the year 20xx, any other digit is the day after February.
        if (d === 0) {
          return startYearFirst(state, index, typed);
        }
        const month = done(state, index, buffer, 2);
        return month.state.focus === index ? month : typeDigit(month.state, digit);
      }
      if (buffer === '0') {
        return d === 0 ? refuse(state, index, 'There is no month 00') : done(state, index, typed, d);
      }
      if (n <= 12) {
        return done(state, index, typed, n);
      }
      if ((n === 19 || n === 20) && dateBlank(state, index)) {
        return startYearFirst(state, index, typed);
      }
      return refuse(state, index, `There is no month ${typed}`);
    }
    case 'day': {
      const limit = dayLimit(state);
      if (!buffer) {
        return d === 0 || d * 10 <= limit ? wait(state, index, typed) : done(state, index, typed, d);
      }
      if (n === 0) {
        return refuse(state, index, 'There is no day 00');
      }
      if (n > 31) {
        return refuse(state, index, `There is no day ${typed}`);
      }
      if (n > limit) {
        const month = valueOf(state, 'month') as number;
        return refuse(state, index, impossibleDateMessage(month, n, month === 2 ? valueOf(state, 'year') : null));
      }
      return done(state, index, typed, n);
    }
    case 'year': {
      if ((buffer === '' && d !== 1 && d !== 2) || (buffer === '1' && d !== 9) || (buffer === '2' && d !== 0)) {
        return refuse(state, index, yearMessage(typed));
      }
      return typed.length === 4 ? done(state, index, typed, n) : wait(state, index, typed);
    }
    case 'hour': {
      if (!buffer) {
        return d <= 1 ? wait(state, index, typed) : done(state, index, typed, d);
      }
      return n >= 1 && n <= 12 ? done(state, index, typed, n) : refuse(state, index, noHourMessage(typed));
    }
    case 'minute': {
      if (!buffer) {
        // 6-9 can only be 06-09: no second digit can follow, so the minute is whole, as a day's 4-9.
        return d <= 5 ? wait(state, index, typed) : done(state, index, `0${digit}`, d);
      }
      return done(state, index, typed, n);
    }
    default:
      return refuse(state, index, 'Type A or P');
  }
};

/** 19 or 20 typed into the month of an empty date, which no month can be: the year, typed first. */
const startYearFirst = (state: FieldState, monthIndex: number, typed: string): SbKeyResult => {
  const year = indexOf(state, 'year');
  let next = withSeg(state, monthIndex, { value: null, typed: '', refused: null });
  next = withSeg(next, year, { value: null, typed, refused: null });
  return { state: { ...next, focus: year, enteredBy: 'advance', fresh: false, yearFirst: 'year' }, announce: YEAR_FIRST_LINE };
};

// ─── Separators and letters ─────────────────────────────────

/** A separator that cannot stand before the segment it would lead into. */
const misplacedMessage = (kind: SegKind, char: string): string => {
  if (char === '.' && DATE_KINDS.has(kind)) {
    return DOT_MESSAGE;
  }
  return `Type the ${kind === 'period' ? 'AM/PM' : SEGMENT_NAME[kind].toLowerCase()} here, not "${char}"`;
};

/**
 * A separator, or (`char` null) an A or P ending the hour or minutes. It only ever leads into the
 * segment that follows in typing order, and only when it can stand before that segment (`LEADS`);
 * anything else is a refused key, so a date typed on from the year never spills into the time.
 */
const typeSeparator = (state: FieldState, char: string | null): SbKeyResult => {
  const index = state.focus;
  const seg = state.segs[index];
  if (seg.refused && !state.fresh) {
    return refuse(state, index, seg.refused);
  }
  if (seg.kind === 'year' && state.yearFirst === 'needsSeparator' && !state.fresh) {
    return char !== null && YEAR_FIRST_SEPARATORS.has(char)
      ? { state: enter({ ...state, yearFirst: 'on' }, indexOf(state, 'month'), 'advance') }
      : refuse(state, index, YEAR_FIRST_MESSAGE);
  }
  if (!state.fresh && seg.value !== null) {
    // After the segment typing completed last: nothing follows it.
    return { state };
  }
  if (state.fresh && state.enteredBy === 'advance') {
    // Typing just moved here: the separator belongs between the segment before and this one.
    return char === null || leads(state, seg.kind).includes(char) ? { state } : refuse(state, index, misplacedMessage(seg.kind, char));
  }
  const value = !state.fresh && seg.typed ? wholeValue(seg.kind, seg.typed) : null;
  if (!state.fresh && seg.typed && value === null) {
    return refuse(state, index, unfinishedMessage(seg.kind, seg.typed));
  }
  const next = nextInOrder(state, index);
  if (char !== null && next >= 0 && !leads(state, state.segs[next].kind).includes(char)) {
    return refuse(state, index, misplacedMessage(state.segs[next].kind, char));
  }
  if (value !== null) {
    return done(state, index, seg.typed, value);
  }
  // A segment the user entered and typed nothing into: move on, leaving it as it is.
  return { state: next < 0 ? state : enter(state, next, 'advance') };
};

/** A or P in the hour or minutes ends that segment, as a separator does, and sets AM or PM. */
const typePeriodLetter = (state: FieldState, pm: boolean): SbKeyResult => {
  const index = state.focus;
  const seg = state.segs[index];
  const period = indexOf(state, 'period');
  if (seg.kind === 'period') {
    if (seg.refused && !state.fresh) {
      return refuse(state, index, seg.refused);
    }
    return { state: withSeg({ ...state, fresh: false }, index, { value: pm ? 1 : 0, typed: '', refused: null }) };
  }
  const ended = typeSeparator(state, null);
  if (ended.refusal) {
    return ended;
  }
  const next = withSeg(ended.state, period, { value: pm ? 1 : 0, typed: '', refused: null });
  const order = typingOrder(next).filter((i) => !DATE_KINDS.has(next.segs[i].kind));
  const empty = order.find((i) => next.segs[i].value === null && !next.segs[i].refused);
  return { state: enter(next, empty ?? period, 'advance') };
};

const typeLetter = (state: FieldState, char: string): SbKeyResult => {
  const index = state.focus;
  const seg = state.segs[index];
  const lower = char.toLowerCase();
  if ((lower === 'a' || lower === 'p') && (seg.kind === 'hour' || seg.kind === 'minute' || seg.kind === 'period')) {
    return typePeriodLetter(state, lower === 'p');
  }
  // The m of "pm": on the AM/PM segment, or right after an A or P moved focus on.
  const period = state.segs[indexOf(state, 'period')];
  if (lower === 'm' && period?.value != null && !period.refused && (seg.kind === 'period' || (state.fresh && state.enteredBy === 'advance'))) {
    return { state };
  }
  if (seg.refused && !state.fresh) {
    return refuse(state, index, seg.refused);
  }
  return refuse(state, index, seg.kind === 'period' ? 'Type A or P' : 'Type digits only');
};

// ─── Steps ──────────────────────────────────────────────────

const mod = (n: number, m: number): number => ((n % m) + m) % m;

const stepYear = (year: number, delta: number): number => {
  if (year < YEAR_MIN) {
    return delta > 0 ? Math.min(year + delta, YEAR_MAX) : year;
  }
  if (year > YEAR_MAX) {
    return delta < 0 ? Math.max(year + delta, YEAR_MIN) : year;
  }
  return Math.min(YEAR_MAX, Math.max(YEAR_MIN, year + delta));
};

const stepValue = (state: FieldState, kind: SegKind, value: number, keyName: string): number => {
  const limit = dayLimit(state);
  const delta = (keyName === 'ArrowUp' || keyName === 'PageUp' ? 1 : -1) * (keyName.startsWith('Page') ? PAGE_STEP[kind] : 1);
  switch (kind) {
    case 'month':
      return mod(value - 1 + delta, 12) + 1;
    case 'day':
      return mod(Math.min(value, limit) - 1 + delta, limit) + 1;
    case 'year':
      return stepYear(value, delta);
    case 'hour':
      return mod(value + delta, 12) || 12;
    case 'minute':
      return mod(value + delta, 60);
    default:
      return 1 - value;
  }
};

/** Today's or now's part, for a step on an empty segment. */
const nowPart = (kind: SegKind, ctx: SbSegContext): number => {
  switch (kind) {
    case 'month':
      return ctx.today.month + 1;
    case 'day':
      return ctx.today.day;
    case 'year':
      return ctx.today.year;
    case 'hour':
      return ctx.now.hour % 12 || 12;
    case 'minute':
      return ctx.now.minute;
    default:
      return ctx.now.hour >= 12 ? 1 : 0;
  }
};

const step = (state: FieldState, keyName: string, ctx: SbSegContext): SbKeyResult => {
  const index = state.focus;
  const seg = state.segs[index];
  // A waiting digit that is a whole value steps from what it shows (`1` in the day, then Up: 2).
  const shown = seg.value ?? (seg.refused ? null : wholeValue(seg.kind, seg.typed));
  const fromNow = shown === null;
  const value = shown === null ? nowPart(seg.kind, ctx) : stepValue(state, seg.kind, shown, keyName);
  let next = withSeg({ ...state, fresh: true }, index, { value, typed: '', refused: null });
  const period = indexOf(next, 'period');
  if (seg.kind === 'hour' && fromNow && next.segs[period].value === null && !next.segs[period].refused) {
    // An hour taken from now brings now's half of the day with it: Up, Up, Tab at 8:15 PM is 8:15 PM.
    next = withSeg(next, period, { value: ctx.now.hour >= 12 ? 1 : 0 });
  }
  return { state: next };
};

// ─── Keys ───────────────────────────────────────────────────

const ACTING_KEYS = new Set(['Backspace', 'Delete', 'ArrowUp', 'ArrowDown']);

/** Kept text gives way to an empty field focused on its first segment, where the edit is then made. */
const leaveKept = (state: FieldState): FieldState => ({ ...emptyState(state.mode), focus: 0 });

/** An explicit move to another segment (Tab, an arrow, Home, End, a click): a waiting whole value completes first. */
const moveTo = (state: FieldState, to: number): FieldState => {
  const moved = completeWaiting(state);
  return to === state.focus ? moved : enter(yearFirstMoved(moved, to), to, 'user');
};

/**
 * The only way a key changes the state. `key` is one character, or Backspace, Delete, ArrowUp,
 * ArrowDown, PageUp, PageDown, Home, End, ArrowLeft, ArrowRight, Tab or ShiftTab. Home and End
 * move to the first and last segment, as they move the caret of a text box; they change no value.
 */
export const key = (input: FieldState, keyName: string, ctx: SbSegContext): SbKeyResult => {
  let state: FieldState = input.rejected ? { ...input, rejected: null } : input;
  const char = keyName.length === 1 ? keyName.normalize('NFKC') : keyName;
  if (state.kept !== null) {
    // A separator has nothing to separate in text kept whole, and no value of its own to replace it with.
    if ((char.length !== 1 && !ACTING_KEYS.has(char)) || SEPARATORS.has(char)) {
      return { state };
    }
    state = leaveKept(state);
    if (char === 'Backspace' || char === 'Delete') {
      return { state };
    }
  }
  if (state.focus < 0) {
    state = enter(state, 0, 'user');
  }
  const index = state.focus;
  const seg = state.segs[index];
  const result = ((): SbKeyResult => {
    if (/^\d$/.test(char)) {
      return typeDigit(state, char);
    }
    if (SEPARATORS.has(char)) {
      return typeSeparator(state, char);
    }
    if (char.length === 1) {
      return typeLetter(state, char);
    }
    switch (char) {
      case 'Backspace':
        if (isBlank(seg)) {
          return { state: index > 0 ? enter(state, index - 1, 'user') : state };
        }
        if (seg.refused && seg.value !== null) {
          // A key refused after a whole value: Backspace takes the key back, and the value stays to be typed over.
          return { state: withSeg({ ...state, fresh: true }, index, { refused: null }) };
        }
        return { state: emptied(state) };
      case 'Delete':
        return { state: emptied(state) };
      case 'ArrowUp':
      case 'ArrowDown':
      case 'PageUp':
      case 'PageDown':
        return step(state, char, ctx);
      case 'Home':
      case 'End':
        return { state: moveTo(state, char === 'Home' ? 0 : state.segs.length - 1) };
      case 'Tab':
      case 'ArrowRight': {
        if (state.yearFirst === 'needsSeparator' && seg.kind === 'year') {
          return { state: enter({ ...state, yearFirst: 'on' }, indexOf(state, 'month'), 'user') };
        }
        if (index + 1 < state.segs.length) {
          return { state: moveTo(state, index + 1) };
        }
        const moved = completeWaiting(state);
        return char === 'Tab' ? { state: moved, exit: 'forward' } : { state: moved };
      }
      case 'ShiftTab':
      case 'ArrowLeft': {
        if (index > 0) {
          return { state: moveTo(state, index - 1) };
        }
        const moved = completeWaiting(state);
        return char === 'ShiftTab' ? { state: moved, exit: 'back' } : { state: moved };
      }
      default:
        return { state };
    }
  })();
  return { ...result, state: periodSettled(settle(result.state)) };
};

/** Backspace or Delete empties the focused segment; an AM/PM emptied so is not one the rule fills (`periodCleared`). */
const emptied = (state: FieldState): FieldState => {
  const seg = state.segs[state.focus];
  const periodCleared = state.periodCleared || (seg.kind === 'period' && seg.value !== null);
  return withSeg({ ...state, fresh: true, periodCleared }, state.focus, { value: null, typed: '', refused: null });
};

/** An AM/PM that holds a value again is no longer one the user emptied. */
const periodSettled = (state: FieldState): FieldState =>
  state.periodCleared && state.segs.some((seg) => seg.kind === 'period' && seg.value !== null) ? { ...state, periodCleared: false } : state;

/** Leaving the year of a date typed year first for another segment goes on year first. */
const yearFirstMoved = (state: FieldState, to: number): FieldState =>
  (state.yearFirst === 'year' || state.yearFirst === 'needsSeparator') && state.segs[to].kind !== 'year' ? { ...state, yearFirst: 'on' } : state;

/** Focus moved to a segment by a click or tap: an explicit move, as Tab is. */
export const focusSegment = (state: FieldState, index: number): FieldState => {
  if (index === state.focus) {
    return state;
  }
  if (state.focus < 0) {
    return { ...enter(state, index, 'user'), rejected: null };
  }
  return settle(moveTo(state, index));
};

/** Focus left the field: nothing is completed (a waiting `1` in the day stays `1d`), and a year-first run is over. */
export const blur = (state: FieldState): FieldState => ({ ...state, focus: -1, fresh: true, rejected: null, yearFirst: 'off' });

/**
 * Commit (leaving the field and its panel, Enter, Done, a pick, a time column): with the hour and
 * minutes whole and AM/PM still empty, the one rule fills it, hours 1-6 and 12 PM, 7-11 AM. An
 * AM/PM the user emptied stays empty, and the entry unfinished: nobody chose PM for a held 5:30 AM.
 */
export const commit = (state: FieldState): { state: FieldState; announce?: string } => {
  if (state.kept !== null || state.mode === 'date') {
    return { state };
  }
  const hour = valueOf(state, 'hour');
  const minute = valueOf(state, 'minute');
  const period = state.segs[indexOf(state, 'period')];
  if (hour === null || minute === null || period.value !== null || period.refused || state.periodCleared) {
    return { state };
  }
  const pm = hour <= 6 || hour === 12;
  const next = withSeg(state, indexOf(state, 'period'), { value: pm ? 1 : 0, typed: '' });
  return { state: next, announce: formatTimeDisplay({ hour: (hour % 12) + (pm ? 12 : 0), minute }) };
};

// ─── Values in, values out ──────────────────────────────────

const setDate = (state: FieldState, date: SbDateParts): FieldState => {
  let next = withSeg(state, indexOf(state, 'month'), { value: date.month + 1, typed: '', refused: null });
  next = withSeg(next, indexOf(next, 'day'), { value: date.day, typed: '', refused: null });
  return withSeg(next, indexOf(next, 'year'), { value: date.year, typed: '', refused: null });
};

const setTime = (state: FieldState, time: SbTimeParts): FieldState => {
  let next = withSeg(state, indexOf(state, 'hour'), { value: time.hour % 12 || 12, typed: '', refused: null });
  next = withSeg(next, indexOf(next, 'minute'), { value: time.minute, typed: '', refused: null });
  return withSeg(next, indexOf(next, 'period'), { value: time.hour >= 12 ? 1 : 0, typed: '', refused: null });
};

/** The field a pick, a column choice or a paste is applied to: kept text gone, every refusal cleared. */
const pickBase = (state: FieldState): FieldState => ({
  ...(state.kept !== null ? leaveKept(state) : state),
  segs: (state.kept !== null ? emptyState(state.mode).segs : state.segs).map((seg) => ({ ...seg, refused: null })),
  yearFirst: 'off',
  rejected: null,
  focus: state.focus,
  fresh: true,
});

/** A field showing `date` and `time` (either may be null to leave that half as it is): a pick or a paste. */
export const withParts = (state: FieldState, date: SbDateParts | null, time: SbTimeParts | null): FieldState => {
  let next = pickBase(state);
  if (date && state.mode !== 'time') {
    next = setDate(next, date);
  }
  if (time && state.mode !== 'date') {
    next = { ...setTime(next, time), periodCleared: false };
  }
  return next;
};

/** The hour (1-12), minute and AM/PM (0 or 1) segments' values; null where a segment is empty. */
export interface SbTimeSegments {
  hour: number | null;
  minute: number | null;
  period: number | null;
}

/** What the time segments hold, refused ones as empty: the panel's columns show them. */
export const timeSegments = (state: FieldState): SbTimeSegments => ({
  hour: state.kept !== null ? null : valueOf(state, 'hour'),
  minute: state.kept !== null ? null : valueOf(state, 'minute'),
  period: state.kept !== null ? null : valueOf(state, 'period'),
});

/**
 * A choice in the panel's time columns sets the segments it names, keeps the others, and commits at
 * once: beside an hour, empty minutes become 00 and an empty AM/PM takes the rule. A minute digit
 * still waiting is not empty: it stays unfinished, as Done leaves it. An hour chosen anew lets the
 * rule fill an AM/PM the user emptied, as an hour typed anew does.
 */
export const withTimeSegments = (state: FieldState, choice: Partial<SbTimeSegments>): FieldState => {
  const base = choice.hour === undefined || choice.hour === null ? pickBase(state) : { ...pickBase(state), periodCleared: false };
  const chosen = (['hour', 'minute', 'period'] as const).reduce((next, kind) => {
    const value = choice[kind];
    return value === undefined || value === null ? next : withSeg(next, indexOf(next, kind), { value, typed: '', refused: null });
  }, base);
  const minute = indexOf(chosen, 'minute');
  const whole = valueOf(chosen, 'hour') !== null && isBlank(chosen.segs[minute])
    ? withSeg(chosen, minute, { value: 0, typed: '', refused: null })
    : chosen;
  return periodSettled(commit(whole).state);
};

/** An empty field (Clear), focus kept where it is. */
export const cleared = (state: FieldState): FieldState => ({ ...emptyState(state.mode), focus: state.focus });

const TYPEABLE_RE = /^[\d/\-.,: Tt aApPmM]+$/;

/**
 * Text inserted in one go. Characters a key could type (digits, separators, a, p, m), arriving
 * other than by a paste or drop (a composition, a phone keyboard's "11", dictated digits,
 * `insertText`), are typed key by key from the focused segment: an input method is a keyboard.
 * Anything else, and every paste or drop, is read whole by the paste reader: a bare time pasted
 * into a datetime replaces only its time (today filling a date that was empty, as over kept text),
 * a bare date only its date. Text the reader does not take changes nothing (kept text stays) and
 * the field says why.
 */
export const insert = (input: FieldState, data: string, ctx: SbSegContext, paste: boolean): SbKeyResult => {
  const text = data.normalize('NFKC').trim();
  if (!paste && text && TYPEABLE_RE.test(text)) {
    return [...text].reduce<SbKeyResult>((result, char) => {
      const next = key(result.state, char, ctx);
      return { ...next, refusal: next.refusal ?? result.refusal, filled: next.filled || result.filled };
    }, { state: input });
  }
  const mode = input.mode;
  let date: SbDateParts | null = null;
  let time: SbTimeParts | null = null;
  if (mode === 'date') {
    // A date and time (copied from a date and time field) gives its date.
    date = parseLooseDate(text) ?? parseLooseDateTime(text)?.date ?? null;
  } else if (!hasZone(text)) {
    const both = parseLooseDateTime(text);
    date = mode === 'time' ? null : both?.date ?? null;
    time = both?.time ?? (mode === 'time' ? parseLooseTime(text) : null);
    // A bare time pasted where no date is given (kept text gives way to an empty field first) takes today.
    if (mode === 'datetime' && time && !date && dateBlank(input)) {
      date = ctx.today;
    }
  }
  if (date || time) {
    const next = withParts({ ...input, focus: Math.max(input.focus, 0) }, date, time);
    const last = time ? indexOf(next, 'period') : indexOf(next, 'year');
    return { state: { ...next, focus: last, enteredBy: 'advance', fresh: true } };
  }
  const why = invalidTextMessage(mode, data.trim());
  return { state: { ...input, rejected: why }, refusal: why };
};

/**
 * The state for a value the page wrote. It replaces everything (slots, kept text, refusals, the
 * year-first mode). Text the field cannot read is kept as it is in a time or datetime field; a
 * bare time or an instant with a zone in a datetime only with `keepUnreadable` (otherwise the time
 * segments show its wall clock). A date field shows only `YYYY-MM-DD` (or, holding a `Date`, also
 * `MM/DD/YYYY`), as the native input did.
 */
export const fromValue = (value: unknown, mode: SbDateTimeMode, valueAs: SbDateValueAs = 'text', keepUnreadable = false): FieldState => {
  const empty = emptyState(mode);
  if (value === null || value === undefined || value === '') {
    return empty;
  }
  if (mode === 'date') {
    const date = valueAs === 'date' ? boundDay(value as Date | string) : parseIsoDate(value);
    return date ? setDate(empty, date) : empty;
  }
  const text = String(value).trim();
  const kept: FieldState = { ...empty, kept: text };
  if (mode === 'time') {
    const time = parseLooseTime(text);
    return time ? setTime(empty, time) : kept;
  }
  if (keepUnreadable && hasZone(text)) {
    return kept;
  }
  const iso = text.match(/^(\d{4}-\d{2}-\d{2})T(\d{1,2}):(\d{2})/);
  if (iso) {
    const date = parseIsoDate(iso[1]);
    const time = { hour: Number(iso[2]), minute: Number(iso[3]) };
    return date && time.hour < 24 && time.minute < 60 ? setTime(setDate(empty, date), time) : kept;
  }
  const dateOnly = parseIsoDate(text);
  if (dateOnly) {
    return setDate(empty, dateOnly);
  }
  const bare = parseLooseTime(text);
  if (bare) {
    return keepUnreadable ? kept : setTime(empty, bare);
  }
  const both = parseLooseDateTime(text);
  return both?.date && both.time ? setTime(setDate(empty, both.date), both.time) : kept;
};

// ─── Reading ────────────────────────────────────────────────

/** What the state holds. Pure and total. */
export const reading = (state: SbFieldStateLike): SbSegReading => {
  if (state.kept !== null) {
    return { kind: 'kept', text: state.kept };
  }
  const refused = state.segs.findIndex((seg) => seg.refused);
  if (refused >= 0) {
    return { kind: 'refused', seg: refused, why: state.segs[refused].refused as string };
  }
  if (state.segs.every(isBlank)) {
    return { kind: 'empty' };
  }
  const get = (kind: SegKind) => state.segs.find((seg) => seg.kind === kind)?.value ?? null;
  const dated = state.mode !== 'time';
  const timed = state.mode !== 'date';
  let date: SbDateParts | null = null;
  if (dated) {
    const [month, day, year] = [get('month'), get('day'), get('year')];
    if (month !== null && day !== null && year !== null) {
      if (day > daysInMonth(year, month - 1)) {
        return { kind: 'impossible', why: impossibleDateMessage(month, day, month === 2 ? year : null) };
      }
      date = { year, month: month - 1, day };
    }
  }
  let time: SbTimeParts | null = null;
  if (timed) {
    const [hour, minute, period] = [get('hour'), get('minute'), get('period')];
    if (hour !== null && minute !== null && period !== null) {
      time = { hour: (hour % 12) + (period ? 12 : 0), minute };
    }
  }
  if (state.mode === 'date') {
    return date ? { kind: 'complete', date, time: null } : { kind: 'incomplete' };
  }
  if (state.mode === 'time') {
    return time ? { kind: 'complete', date: null, time } : { kind: 'incomplete' };
  }
  if (date && (time || timeBlank(state as FieldState))) {
    return { kind: 'complete', date, time };
  }
  return { kind: 'incomplete' };
};

type SbFieldStateLike = Pick<FieldState, 'mode' | 'segs' | 'kept'>;

/** The reading in the shape the value contracts take (`textValue`). */
export const fieldReading = (state: FieldState): SbFieldReading => {
  const read = reading(state);
  if (read.kind === 'empty') {
    return { empty: true, date: null, time: null, complete: true };
  }
  return read.kind === 'complete'
    ? { empty: false, date: read.date, time: read.time, complete: true }
    : { empty: false, date: null, time: null, complete: false };
};

/** The date and time the panel shows as chosen: each half that is whole, whatever the other half holds. */
export const partsShown = (state: FieldState): { date: SbDateParts | null; time: SbTimeParts | null } => {
  if (state.kept !== null) {
    return { date: null, time: null };
  }
  const get = (kind: SegKind) => valueOf(state, kind);
  const [month, day, year] = [get('month'), get('day'), get('year')];
  const date = month !== null && day !== null && year !== null && day <= daysInMonth(year, month - 1) ? { year, month: month - 1, day } : null;
  const [hour, minute, period] = [get('hour'), get('minute'), get('period')];
  const time = hour !== null && minute !== null && period !== null ? { hour: (hour % 12) + (period ? 12 : 0), minute } : null;
  return { date, time };
};

// ─── What the field shows ───────────────────────────────────

export interface SegView {
  /** The digits shown (written as the segment's text). */
  text: string;
  /** The format letters after them (drawn by CSS, never in the text). */
  rest: string;
}

/** A segment's digits and letters. The hour pads only while the field is focused. */
export const segView = (seg: Seg, focused: boolean): SegView => {
  if (seg.value === null) {
    return { text: seg.typed, rest: SEGMENT_PLACEHOLDER[seg.kind].slice(seg.typed.length) };
  }
  switch (seg.kind) {
    case 'year':
      return { text: String(seg.value), rest: '' };
    case 'hour':
      return { text: focused ? pad(seg.value) : String(seg.value), rest: '' };
    case 'period':
      return { text: seg.value ? 'PM' : 'AM', rest: '' };
    default:
      return { text: pad(seg.value), rest: '' };
  }
};

/**
 * Which segments and literals show. Focused, every one, letters for what is empty. At rest an
 * empty field shows nothing, and a datetime whose time is empty shows its date alone. An empty
 * date stays, letters and all, beside a time: a time typed with no date is not a value.
 */
export const visibleParts = (state: FieldState, focused: boolean): { segs: boolean[]; literals: boolean[] } => {
  const all = state.segs.map(() => true);
  const literals = LITERALS[state.mode].map(() => true);
  if (focused) {
    return { segs: all, literals };
  }
  if (state.segs.every(isBlank)) {
    return { segs: all.map(() => false), literals: literals.map(() => false) };
  }
  if (state.mode === 'datetime' && timeBlank(state)) {
    return { segs: state.segs.map((seg) => DATE_KINDS.has(seg.kind)), literals: literals.map((_, i) => i < 2) };
  }
  return { segs: all, literals };
};

/** The text the field shows, letters included (`10/1d/2026`, `9:3m PM`): what messages quote. */
export const displayText = (state: FieldState, focused = false): string => {
  if (state.kept !== null) {
    return state.kept;
  }
  const shown = visibleParts(state, focused);
  return state.segs.map((seg, i) => {
    const view = segView(seg, focused);
    const text = shown.segs[i] ? view.text + view.rest : '';
    return text + (shown.literals[i] ? LITERALS[state.mode][i] : '');
  }).join('');
};

/** What a screen reader hears for a segment's value: what it shows, a waiting or refused digit included. */
export const segValueText = (state: FieldState, index: number): string => {
  const seg = state.segs[index];
  if (seg.value === null) {
    return seg.typed || 'Empty';
  }
  switch (seg.kind) {
    case 'month':
      return MONTH_NAMES[seg.value - 1] ?? String(seg.value);
    case 'minute':
      return pad(seg.value);
    case 'hour': {
      const period = valueOf(state, 'period');
      return period === null ? String(seg.value) : `${seg.value} ${period ? 'PM' : 'AM'}`;
    }
    case 'period':
      return seg.value ? 'PM' : 'AM';
    default:
      return String(seg.value);
  }
};

/** A segment's bounds for `aria-valuemin` / `aria-valuemax`. */
export const segBounds = (state: FieldState, index: number): [number, number] => {
  const kind = state.segs[index].kind;
  const bounds: Record<SegKind, [number, number]> = { month: [1, 12], day: [1, dayLimit(state)], year: [YEAR_MIN, YEAR_MAX], hour: [1, 12], minute: [0, 59], period: [0, 1] };
  return bounds[kind];
};

/** Lower-case first letter, for a reason quoted inside a message; a month's name keeps its capital. */
const quoted = (why: string): string =>
  MONTH_NAMES.some((name) => why.startsWith(name)) ? why : why.charAt(0).toLowerCase() + why.slice(1);

/**
 * The message under the field, a pure function of the state: a refused key (with how to go on
 * while focused), an impossible date, then an unfinished entry when `unfinished` says it is
 * reported (the field was left, or Enter or Done committed it).
 */
export const message = (state: FieldState, focused: boolean, unfinished: boolean): string => {
  if (state.rejected && focused) {
    return state.rejected;
  }
  const read = reading(state);
  switch (read.kind) {
    case 'refused':
      return focused ? `${read.why}${BLOCKED_SUFFIX}` : invalidTextMessage(state.mode, quoted(read.why));
    case 'impossible':
      return read.why;
    case 'incomplete':
      return unfinished ? invalidTextMessage(state.mode, displayText(state)) : '';
    default:
      return '';
  }
};

/** The muted line under a field while a date is being typed year first. */
export const notice = (state: FieldState, focused: boolean): string =>
  focused && (state.yearFirst === 'year' || state.yearFirst === 'needsSeparator') ? YEAR_FIRST_LINE : '';

/**
 * Whether two states show the same thing: the same values, waiting digits and refusals. Undo history
 * steps only on a real change, and the host writes a value only then.
 */
export const sameEntry = (a: FieldState, b: FieldState): boolean =>
  a.kept === b.kept
  && a.segs.length === b.segs.length
  && a.segs.every((seg, i) => seg.value === b.segs[i].value && seg.refused === b.segs[i].refused && (seg.value !== null || seg.typed === b.segs[i].typed));

/** A digit waits in a segment: the entry is half typed, not a state undo goes back to. */
export const waiting = (state: FieldState): boolean => state.segs.some((seg) => seg.value === null && !!seg.typed && !seg.refused);

/** Whether two states hold the same date and time (a held value typed back as it was is that value again). */
export const sameValue = (a: FieldState, b: FieldState): boolean =>
  a.kept === b.kept && a.segs.every((seg, i) => seg.value === b.segs[i]?.value && !seg.refused && !b.segs[i].refused && (seg.value !== null || (!seg.typed && !b.segs[i].typed)));
